import React, { useState, useRef, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  Button,
  Card,
  Form,
  message,
  Spin,
} from 'antd'
import CoverageArea from '@reggora-app/coverage-map'
import {
  AMC,
  COMPANY,
  USER_INFORMATION_UPDATE,
  APPRAISER,
  MANAGER,
  SOLE_PROPRIETOR,
} from '@components/Signup/constants'
import {
  ERROR,
  SUCCEEDED,
} from '@constants/fetchStatuses'
import { getMapOverlay } from '@actions/map'
import { selectZipOverlay, selectCountyOverlay } from '@reducers/map/mapSelectors'
import {
  selectAddresses,
  selectCoverageData,
  selectCurrentUserId,
  selectCurrentUserCompanyId,
  selectCurrentUserCompanyMembers,
  selectCurrentUserStatus,
  selectInvitationStatus,
  selectPhoneInfo,
} from '@reducers/currentUser/currentUserSelectors'
import {
  buildCompanyData,
  buildNestedFormObject,
  openErrorNotification,
} from '@components/Signup/SignupWizard/helpers'
import {
  createInvitation,
  updateCompany,
  updateUser,
  clearStatus,
} from '@actions/currentUser'
import useSignupAnalyticsTracker from '@components/Signup/SegmentAnalytics/useSignupAnalyticsTracker'
import useSignupPageView from '@components/Signup/SegmentAnalytics/useSignupPageView'
import CompanyMember from './CompanyMember'
import AddressForm from '@components/Shared/FormComponents/AddressForm'
import PhoneFormFields from '@components/Shared/FormComponents/PhoneFormFields'
import './CompanyOrCoverage.scss'

const CompanyOrCoverage = ({
  userType,
  goToNextStep,
  goToPreviousStep,
  nextStepName,
  stepName,
  defaultState,
}) => {
  useSignupPageView('Account Setup Company / Coverage')
  const track = useSignupAnalyticsTracker()
  const { companyAddress, primaryAddress } = useSelector(selectAddresses)
  const { phone, phoneType } = useSelector(selectPhoneInfo)
  const {
    coverage: initialCoverage,
    coverageLevel: initialCoverageLevel,
    companyCoverage,
  } = useSelector(selectCoverageData)
  const currentUserStatus = useSelector(selectCurrentUserStatus)
  const invitationStatus = useSelector(selectInvitationStatus)
  const companyId = useSelector(selectCurrentUserCompanyId)
  const currentUserId = useSelector(selectCurrentUserId)
  const zipOverlay = useSelector(selectZipOverlay)
  const countyOverlay = useSelector(selectCountyOverlay)
  const [companyMemberIndices, setCompanyMemberIndices] = useState({ 0: true })
  const {
    appraisers: invitedAppraisers,
    managers: invitedManagers,
  } = useSelector(selectCurrentUserCompanyMembers)


  const [{ coverage, coverageLevel }, setCoverage] = useState({
    coverage: initialCoverage,
    coverageLevel: initialCoverageLevel,
  })
  const [coverageError, setCoverageError] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [invitedAppraisersToDisplay, setInvitedAppraisersToDisplay] = useState([])
  const [invitedManagersToDisplay, setInvitedManagersToDisplay] = useState([])
  const formRef = useRef(null)
  const dispatch = useDispatch()

  const isCompany = userType === COMPANY || userType === AMC
  const isSoleProprietor = userType === SOLE_PROPRIETOR

  useEffect(() => {
    if (!submitting) {
      setInvitedAppraisersToDisplay(invitedAppraisers)
      setInvitedManagersToDisplay(invitedManagers)
    }
  }, [invitedAppraisers, invitedManagers, submitting])

  useEffect(() => {
    dispatch(clearStatus())
  }, [dispatch])

  useEffect(() => {
    if (coverageError && coverage.length) {
      setCoverageError(false)
    }
  }, [coverage, setCoverageError, coverageError])

  useEffect(() => {
    // Block for user submit functionality
    if (isCompany || isSoleProprietor || !submitting) { return }

    if (currentUserStatus === SUCCEEDED) {
      goToNextStep()
    } else if (currentUserStatus === ERROR) {
      setSubmitting(false)
      openErrorNotification()
    }
  }, [currentUserStatus, submitting, goToNextStep, isCompany, isSoleProprietor])

  useEffect(() => {
    // Block for company submit functionality
    if (!submitting || (!isCompany && !isSoleProprietor)) { return }
    // If both calls succeeded, or if one did and the other was not made
    if (currentUserStatus === SUCCEEDED && (!invitationStatus || invitationStatus === SUCCEEDED)) {
      goToNextStep()
    } else if (currentUserStatus === ERROR || invitationStatus === ERROR) {
      setSubmitting(false)
      openErrorNotification()
    }
  }, [isCompany, currentUserStatus, invitationStatus, submitting, goToNextStep, isSoleProprietor])

  const clearFieldErrors = changedValues => {
    const updatedFields = Object.keys(changedValues)
      .filter(name => formRef.current.getFieldError(name).length)
      .map(name => ({ name, errors: [] }))
    formRef.current.setFields(updatedFields)
  }

  const submitUserData = ({ formValues }) => {
    const formData = buildNestedFormObject(formValues)
    const userData = {
      ...formData,
      coverage: coverage,
      coverage_level: coverageLevel,
    }
    const trackingPayload = {
      state: userData.primary_address.state,
      city: userData.primary_address.city,
    }
    track({ eventName: `Account Setup ${nextStepName} Button Clicked ${stepName}`, payload: trackingPayload })
    dispatch(updateUser(userData, USER_INFORMATION_UPDATE))
    setSubmitting(true)
  }

  const submitCompanyData = ({ formValues }) => {
    const { companyData, invitationsData } = buildCompanyData({
      formValues,
      companyMemberIndices,
      coverage,
      isSoleProprietor,
    })
    const trackingPayload = {
      state: companyData.state,
      city: companyData.city,
    }

    track({ eventName: `Account Setup ${nextStepName} Button Clicked ${stepName}`, payload: trackingPayload })
    if (invitationsData.length > 0) {
      dispatch(createInvitation(invitationsData, companyId))
    }
    dispatch(updateCompany(companyData, companyId))
    setSubmitting(true)
  }

  const handleContinue = async () => {
    try {
      const formValues = await formRef.current.validateFields()
      if (!coverage.length) {
        throw Error()
      }
      isCompany || isSoleProprietor
        ? submitCompanyData({ formValues })
        : submitUserData({ formValues })
    } catch ({ errorFields = [] }) {
      // Custom error for missing coverage
      const missingCoverage = !coverage.length
      if (errorFields.length || missingCoverage) {
        const numErrors = missingCoverage
          ? errorFields.length + 1
          : errorFields.length
        const errorText = numErrors > 1 ? 'errors' : 'error'
        message.error({
          content: `${numErrors} ${errorText} found`,
          style: { marginTop: '70vh' },
        })
      }
      if (missingCoverage) {
        setCoverageError(true)
      }
    }
  }

  const handleBack = () => {
    track({ eventName: `Account Setup Back Button Clicked ${stepName}` })
    goToPreviousStep()
  }

  const addCompanyMember = () => {
    track({ eventName: 'Account Setup Add Company Member Clicked' })
    const nextIndex = Math.max(...Object.keys(companyMemberIndices)) + 1
    setCompanyMemberIndices(prevState => ({
      ...prevState,
      [nextIndex]: true,
    }))
  }

  const removeCompanyMember = index => {
    track({ eventName: 'Account Setup Remove Company Member Clicked' })
    const updatedCompanyMemberIndices = { ...companyMemberIndices }
    delete updatedCompanyMemberIndices[index]
    setCompanyMemberIndices(updatedCompanyMemberIndices)
  }

  const validEmailToInvite = email => {
    return ![...invitedAppraisers, ...invitedManagers].map(({ email }) => email).includes(email)
  }

  const companyMemberComponents = Object.keys(companyMemberIndices).map((index) => (
    <CompanyMember
      className='company-member-form'
      index={index}
      key={index}
      removeCompanyMember={removeCompanyMember}
      validEmailToInvite={validEmailToInvite}
    />
  ))

  const invitedAppraiserComponents = invitedAppraisersToDisplay
    .filter(({ id }) => id !== currentUserId)
    .map(({ email }) => (
      <CompanyMember
        className='company-member-form'
        key={email}
        email={email}
        role={APPRAISER}
        validEmailToInvite={validEmailToInvite}
      />
    ))

  const invitedManagerComponents = invitedManagersToDisplay
    .filter(({ id }) => id !== currentUserId)
    .map(({ email }) => (
      <CompanyMember
        className='company-member-form'
        key={email}
        email={email}
        role={MANAGER}
        validEmailToInvite={validEmailToInvite}
      />
    ))

  return (
    <Form
      className='company-or-coverage'
      ref={formRef}
      validateTrigger='onSubmit'
      onValuesChange={clearFieldErrors}
      data-testid="signup-company-step"
    >
      <Card>
        <h3>{isCompany ? 'Company Information' : 'Contact Information'}</h3>
        <AddressForm
          initialValues={companyAddress || primaryAddress || {}}
          addressPrefix={isCompany || isSoleProprietor ? '' : 'primary_address.'}
        />
        <PhoneFormFields
          formRef={formRef}
          initialValues={{
            phone,
            phoneType,
          }}
        />
      </Card>
      <CoverageArea
        showNationalView={isCompany}
        onCoverageChange={setCoverage}
        showError={coverageError}
        currentCoverageInfo={{
          selectedCoverage: coverage,
          selectedCoverageLevel: coverageLevel,
        }}
        coverageOverrideInfo={{
          coverageOverride: companyCoverage,
          coverageOverrideLevel: COMPANY,
        }}
        defaultStateCode={defaultState}
        getMapOverlayAction={getMapOverlay}
        zipOverlay={zipOverlay}
        countyOverlay={countyOverlay}
      />
      {isCompany && (
        <Card>
          <h3>Add Company Members (Optional)</h3>
          {invitedAppraiserComponents}
          {invitedManagerComponents}
          {companyMemberComponents}
          <Button
            type='link'
            onClick={addCompanyMember}
          >
            + Company Member
          </Button>
        </Card>
      )}
      <Card className='controls'>
          <Button type='default' onClick={handleBack} data-testid="signup-company-back-button">Back</Button>
          <Button
            type='primary'
            onClick={handleContinue}
            disabled={submitting}
            data-testid="signup-company-continue-button"
          >
            {submitting ? <Spin/> : nextStepName}
          </Button>
      </Card>
    </Form>
  )
}

export default CompanyOrCoverage
